import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiInternalDrawer,
  MiModalConfirm,
} from "@miplatform/components";
import {
  ACCOUNT_TYPE,
  ACTIONS_LIST_TYPE,
  PERMISSIONS,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { Colors } from "@miplatform/types";
import { mdiArrowCollapse, mdiClose, mdiHome } from "@mdi/js";
import { Box } from "@mui/material";
import {
  AddHomeForm,
  AssignmentCreation,
  HomeAndAccountSearch,
  OrderTemplateSelection,
  useAddHomeForm,
} from "../../../../components";
import { usePermissions } from "../../../../hooks";
import {
  CREATE_ORDER_DRAWER_PAGES,
  CREATE_ORDER_DRAWER_TITLE,
  CREATE_ORDER_DRAWER_WIDTH,
  MINIMIZE_CREATE_ORDER_DRAWER_CONFIG,
} from "./createNewOrderDrawerConstants";

/**
 * Props for the CreateNewOrderDrawer component.
 *
 * @typedef {Object} CreateNewOrderDrawerProps
 * @property {boolean} open - Controls whether the drawer is open.
 * @property {() => void} [onClose] - Callback when the drawer is closed.
 * @property {string} [defaultActiveTab] - Optional. Initial order-type tab in the order template selection step (e.g. ORDER_TYPES.SERVICE).
 * @property {string} [defaultPropertyType] - Optional. Initial property type for the new home form (e.g. PROPERTY_TYPE.SERVICE).
 */

/**
 * Drawer for creating a new order: search/select home, pick order template, then create assignment.
 * Supports home search, new home creation, order template selection, and assignment creation.
 *
 * @param {CreateNewOrderDrawerProps} props
 * @returns {JSX.Element}
 */
export const CreateNewOrderDrawer = ({
  open,
  onClose = () => {},
  defaultActiveTab,
  defaultPropertyType,
}) => {
  const { hasPermission } = usePermissions();

  const { theme, classes } = useStyles();
  const [activeScreen, setActiveScreen] = useState(
    CREATE_ORDER_DRAWER_PAGES.HOME_SEARCH
  );
  const [selectedHome, setSelectedHome] = useState({});
  const [selectedOrderTemplate, setSelectedOrderTemplate] = useState({});
  const [isMinimized, turnOnMinimize, turnOffMinimize] = useBoolean();
  const [isConfirmModalOpen, handleConfirmModalOpen, handleConfirmModalClose] =
    useBoolean();
  const { title: modalTitle, description: modalDescription } =
    MINIMIZE_CREATE_ORDER_DRAWER_CONFIG[activeScreen] ||
    MINIMIZE_CREATE_ORDER_DRAWER_CONFIG.default;

  const icon =
    activeScreen === CREATE_ORDER_DRAWER_PAGES.CREATE_HOME ? mdiHome : null;
  const title =
    activeScreen === CREATE_ORDER_DRAWER_PAGES.CREATE_ASSIGNMENT
      ? selectedHome.address?.addressLine1
      : CREATE_ORDER_DRAWER_TITLE[activeScreen];

  const subtitle =
    activeScreen === CREATE_ORDER_DRAWER_PAGES.CREATE_ASSIGNMENT
      ? [
          `${selectedHome.address?.city}, ${selectedHome.address?.regionCode}`,
          `${selectedHome.community || ""}`,
          `${selectedHome.builder || ""}`,
        ]
      : [];

  const goBack = () => {
    setActiveScreen(CREATE_ORDER_DRAWER_PAGES.HOME_SEARCH);
    setSelectedOrderTemplate({});
  };

  const onNewHomeCreation = (home) => {
    setSelectedHome(home);
    setActiveScreen(CREATE_ORDER_DRAWER_PAGES.ORDER_TEMPLATE_SELECTION);
  };

  const handleOrderTemplateSelection = (orderTemplate) => {
    setSelectedOrderTemplate(orderTemplate);
    setActiveScreen(CREATE_ORDER_DRAWER_PAGES.CREATE_ASSIGNMENT);
  };

  const homeForm = useAddHomeForm({
    isCustomerTypeReadOnly: true,
    propertyType: defaultPropertyType,
    filterByAccountTypes: [ACCOUNT_TYPE.SERVICE, ACCOUNT_TYPE.BUILDER],
    onCreate: onNewHomeCreation,
  });

  const handleClose = () => {
    homeForm.onResetForm();
    turnOffMinimize();
    goBack();
    onClose();
  };

  const handleMinimize = () => {
    turnOnMinimize();
    onClose();
  };

  const actions = [
    {
      name: "Minimize",
      icon: mdiArrowCollapse,
      disabled: ![
        CREATE_ORDER_DRAWER_PAGES.CREATE_ASSIGNMENT,
        CREATE_ORDER_DRAWER_PAGES.CREATE_HOME,
      ].includes(activeScreen),
      onClick: handleConfirmModalOpen,
    },
    {
      name: "Close",
      icon: mdiClose,
      onClick: handleClose,
    },
  ];

  return (
    <MiInternalDrawer
      open={open}
      unmountOnExit={!isMinimized}
      className={classes.drawer}
      removeHeaderMargin
      width={CREATE_ORDER_DRAWER_WIDTH}
      horizontalTitles
      title={<span className={classes.title}>{title}</span>}
      leftIcon={icon}
      subtitle={subtitle}
      rightAction={
        <MiActions
          type={ACTIONS_LIST_TYPE.ICONS}
          actions={actions}
          buttonsProps={{ sx: { padding: 1 } }}
        />
      }
    >
      <Box className={classes.container}>
        {activeScreen === CREATE_ORDER_DRAWER_PAGES.HOME_SEARCH && (
          <HomeAndAccountSearch
            type="home"
            hideCreateNewOption={
              !(
                hasPermission(PERMISSIONS.HOME.CREATE) ||
                hasPermission(PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION)
              )
            }
            onCreate={() =>
              setActiveScreen(CREATE_ORDER_DRAWER_PAGES.CREATE_HOME)
            }
            onSelect={(option) => {
              setSelectedHome(option);
              setActiveScreen(
                CREATE_ORDER_DRAWER_PAGES.ORDER_TEMPLATE_SELECTION
              );
            }}
            showLogo
          />
        )}
        {activeScreen === CREATE_ORDER_DRAWER_PAGES.CREATE_HOME && (
          <AddHomeForm
            hidePropertyType={!!defaultPropertyType}
            {...homeForm}
            onCancel={goBack}
            onCreate={homeForm.handleCreateHome}
          />
        )}
        {activeScreen ===
          CREATE_ORDER_DRAWER_PAGES.ORDER_TEMPLATE_SELECTION && (
          <OrderTemplateSelection
            home={selectedHome}
            selectedOrderTemplate={selectedOrderTemplate}
            handleOrderTemplateSelection={handleOrderTemplateSelection}
            goBack={goBack}
            defaultActiveTab={defaultActiveTab}
          />
        )}
        {activeScreen === CREATE_ORDER_DRAWER_PAGES.CREATE_ASSIGNMENT && (
          <AssignmentCreation
            home={selectedHome}
            selectedOrderTemplate={selectedOrderTemplate}
            onCancel={() => {
              setActiveScreen(
                CREATE_ORDER_DRAWER_PAGES.ORDER_TEMPLATE_SELECTION
              );
              setSelectedOrderTemplate({});
            }}
            onSubmit={handleClose}
          />
        )}
      </Box>
      <MiModalConfirm
        title={modalTitle}
        titleIcon={{
          path: mdiArrowCollapse,
          color: theme.palette.primary.main,
        }}
        textPositive="Minimize"
        open={isConfirmModalOpen}
        handleNegative={handleConfirmModalClose}
        handlePositive={() => {
          handleMinimize();
          handleConfirmModalClose();
        }}
        description={modalDescription}
        buttonPositiveColor={Colors.Primary}
      />
    </MiInternalDrawer>
  );
};

const useStyles = makeStyles()((theme) => ({
  drawer: {
    borderLeft: `${theme.spacing(2)} solid ${theme.palette.grey.accent}`,
    boxSizing: "content-box",
    overflow: "auto",
  },
  container: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  title: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
  subTitle: {
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.small,
  },
}));
